import React, { useState } from "react";
import { CircularProgress, TextField, Box, Typography } from "@mui/material";
import { MarkEmailReadOutlined } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { Link as RouterLink } from "react-router-dom";
import { logo } from "../../assets";
import { forgot_password_api } from "../../DAL/Auth/PasswordReset";
import {
  authKeyframes,
  authInputSx,
  authPageSx,
  authCardSx,
  authButtonStyle,
} from "./authStyles";

export default function ForgotPassword() {
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await forgot_password_api(email.trim());

      // The endpoint answers identically whether or not the address matched, so
      // that this page can't be used to find out who has an XAir account. Show
      // the same confirmation for the same reason.
      if (result?.code === 200) {
        setSent(true);
      } else {
        enqueueSnackbar(result?.message || "Something went wrong", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(error?.message || "Something went wrong", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={authPageSx}>
      <style>{authKeyframes}</style>

      <Box sx={{
        position: "absolute", top: "8%", left: "8%", width: 340, height: 340, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(192,30,46,0.45), transparent 70%)",
        filter: "blur(10px)", animation: "floatOrb1 12s ease-in-out infinite", pointerEvents: "none",
      }} />
      <Box sx={{
        position: "absolute", bottom: "5%", right: "10%", width: 420, height: 420, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(30,58,95,0.55), transparent 70%)",
        filter: "blur(10px)", animation: "floatOrb2 14s ease-in-out infinite", pointerEvents: "none",
      }} />

      <Box sx={authCardSx}>
        <Box sx={{ mb: 3.5, textAlign: "center" }}>
          <img
            src={logo}
            alt="XAir"
            style={{ height: 32, width: "auto", animation: "logoGlow 3s ease-in-out infinite" }}
          />
        </Box>

        {sent ? (
          <>
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <MarkEmailReadOutlined sx={{ fontSize: 44, color: "#ff6b7a" }} />
            </Box>
            <Typography sx={{
              fontSize: "22px", fontWeight: 800, color: "#fff", fontFamily: "'Rubik', sans-serif",
              mb: 1, textAlign: "center", letterSpacing: "-0.4px",
            }}>
              Check your inbox
            </Typography>
            <Typography sx={{
              fontSize: "13px", color: "rgba(255,255,255,0.55)", fontFamily: "'Rubik', sans-serif",
              mb: 3, textAlign: "center", lineHeight: 1.7,
            }}>
              If an account exists for <strong style={{ color: "rgba(255,255,255,0.8)" }}>{email}</strong>,
              we've sent a link to reset your password. It expires in an hour.
            </Typography>
            <Typography sx={{
              fontSize: "12px", color: "rgba(255,255,255,0.4)", fontFamily: "'Rubik', sans-serif",
              mb: 3, textAlign: "center", lineHeight: 1.7,
            }}>
              Nothing arrived? Check your spam folder, or{" "}
              <span
                onClick={() => setSent(false)}
                style={{ color: "#ff6b7a", fontWeight: 600, cursor: "pointer" }}
              >
                try a different address
              </span>.
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={{
              fontSize: "22px", fontWeight: 800, color: "#fff", fontFamily: "'Rubik', sans-serif",
              mb: 0.5, textAlign: "center", letterSpacing: "-0.4px",
            }}>
              Forgot your password?
            </Typography>
            <Typography sx={{
              fontSize: "13px", color: "rgba(255,255,255,0.5)", fontFamily: "'Rubik', sans-serif",
              mb: 3.5, textAlign: "center", lineHeight: 1.6,
            }}>
              Enter the email you sign in with and we'll send you a reset link.
            </Typography>

            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  fullWidth
                  size="small"
                  sx={authInputSx}
                />

                <button type="submit" disabled={isLoading} style={authButtonStyle(isLoading)}>
                  {isLoading
                    ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                    : "Send reset link"}
                </button>
              </Box>
            </form>
          </>
        )}

        <Typography sx={{
          textAlign: "center", color: "rgba(255,255,255,0.45)", fontSize: "12px",
          fontFamily: "'Rubik', sans-serif", mt: 3,
        }}>
          <RouterLink to="/login" style={{ color: "#ff6b7a", fontWeight: 600, textDecoration: "none" }}>
            Back to sign in
          </RouterLink>
        </Typography>
      </Box>
    </Box>
  );
}
